import React, { useState } from 'react';
import { Form, Button, Spinner, Alert, ListGroup, Badge, Table } from 'react-bootstrap';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import axios from 'axios';
import config from '../../config'; // Importar a configuração
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faUpload, faDownload } from '@fortawesome/free-solid-svg-icons';

const ImportacaoPacientesTab = ({ onClose, onImportComplete }) => {
    const { currentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();

    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validação do tipo de arquivo
            const allowedTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ];
            if (!allowedTypes.includes(file.type)) {
                showAlertModal('Erro', 'Tipo de arquivo inválido. Por favor, selecione um arquivo Excel (.xls ou .xlsx).', 'danger');
                event.target.value = ''; // Limpa o input
                return;
            }
            setSelectedFile(file);
            setImportResult(null); // Limpa resultados anteriores ao selecionar novo arquivo
        }
    };

    const handleImport = async () => {
        if (!selectedFile) {
            showAlertModal('Atenção', 'Por favor, selecione um arquivo para importar.', 'warning');
            return;
        }
        if (!currentEntidade?.id) {
            showAlertModal('Erro', 'Nenhuma entidade selecionada. Não é possível importar.', 'danger');
            return;
        }

        setIsUploading(true);
        setImportResult(null);

        const formData = new FormData();
        formData.append('documento', selectedFile);
        formData.append('entidade_id', currentEntidade.id);

        try {
            // BACKEND: Rota para processar a importação
            const response = await axios.post('/api/pacientes/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setImportResult(response.data);
            // Notifica o componente pai que a importação (e possível criação de pacientes) foi concluída
            if (response.data.success_count > 0 && onImportComplete) {
                onImportComplete(); // Isso deve acionar a re-busca da lista de pacientes no componente pai
            }

        } catch (error) {
            const errorMessage = error.response?.data?.message || "Ocorreu um erro ao processar o arquivo.";
            showAlertModal('Erro na Importação', errorMessage, 'danger');
            console.error("Erro na importação:", error.response || error);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div>
            <Alert variant="info">
                <Alert.Heading><FontAwesomeIcon icon={faFileExcel} className="me-2" />Instruções para Importação de Pacientes</Alert.Heading>
                <p>
                    Para importar pacientes, crie um arquivo Excel (.xlsx ou .xls) com as seguintes colunas na primeira linha, exatamente como descrito abaixo.
                </p>
                <Table striped bordered hover size="sm" className="mt-3 bg-white">
                    <thead>
                        <tr>
                            <th>Nome da Coluna</th>
                            <th>Regras de Preenchimento</th>
                            <th>Exemplo</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Nome Completo</td>
                            <td><strong>Obrigatório</strong>. Texto com no máximo 255 caracteres.</td>
                            <td>João da Silva</td>
                        </tr>
                        <tr>
                            <td>CPF</td>
                            <td><strong>Obrigatório</strong> e único no sistema. Pontos e traços são permitidos.</td>
                            <td>123.456.789-00</td>
                        </tr>
                        <tr>
                            <td>Email</td>
                            <td><strong>Obrigatório</strong>, formato de e-mail válido e único no sistema.</td>
                            <td>joao.silva@email.com</td>
                        </tr>
                        <tr>
                            <td>Celular (com DDD)</td>
                            <td><strong>Obrigatório</strong>. Incluir o DDD. Apenas números serão considerados.</td>
                            <td>(61) 98877-6655</td>
                        </tr>
                        <tr>
                            <td>Data de Nascimento (dd/mm/aaaa)</td>
                            <td>Opcional. Usar o formato Dia/Mês/Ano (ex: 25/01/1990).</td>
                            <td>25/01/1990</td>
                        </tr>
                        <tr>
                            <td>Sexo (M, F ou O)</td>
                            <td>Opcional. Usar 'M' para Masculino, 'F' para Feminino ou 'O' para Outro.</td>
                            <td>M</td>
                        </tr>
                    </tbody>
                </Table>
                <p className="mt-3 mb-0">
                    Após criar e preencher o arquivo, selecione-o no campo abaixo e clique em "Importar".
                </p>
            </Alert>

            <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>Selecione o arquivo Excel (.xlsx)</Form.Label>
                <Form.Control type="file" onChange={handleFileChange} accept=".xlsx, .xls" />
            </Form.Group>

            <div className="d-flex justify-content-end">
                <Button variant="secondary" onClick={onClose} className="me-2" disabled={isUploading}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleImport} disabled={!selectedFile || isUploading}>
                    {isUploading ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faUpload} />}
                    {isUploading ? ' Importando...' : ' Importar'}
                </Button>
            </div>

            {importResult && (
                <div className="mt-4">
                    <hr />
                    <h4 className="mb-3">Resultado da Importação</h4>

                    {/* Seção de Sucessos */}
                    {importResult.successes && importResult.successes.length > 0 && (
                        <>
                            <Alert variant="success">
                                <strong>{importResult.success_count}</strong> paciente(s) importado(s) com sucesso.
                            </Alert>
                            <h6>Detalhes dos Sucessos:</h6>
                            <ListGroup style={{ maxHeight: '200px', overflowY: 'auto' }} className="mb-3">
                                {importResult.successes.map((success, index) => (
                                    <ListGroup.Item key={`success-${index}`} variant="success">
                                        <strong>Linha {success.row}:</strong> {success.nome} (CPF: {success.cpf})
                                        <Badge bg="success" className="ms-2 float-end">SUCESSO</Badge>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </>
                    )}

                    {/* Seção de Falhas */}
                    {importResult.failures && importResult.failures.length > 0 && (
                        <>
                            <Alert variant="danger">
                                <strong>{importResult.failure_count}</strong> registro(s) não puderam ser importados.
                            </Alert>
                            <h6>Detalhes dos Erros:</h6>
                            <ListGroup style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {importResult.failures.map((failure, index) => (
                                    <ListGroup.Item key={`failure-${index}`} variant="danger">
                                        <strong>Linha {failure.row}:</strong> {failure.nome || 'Nome não informado'} (CPF: {failure.cpf || 'N/A'})
                                        <Badge bg="danger" className="ms-2 float-end">ERRO</Badge>
                                        <div className="text-dark small mt-1">{failure.errors.join(', ')}</div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </>
                    )}

                    {/* Mensagem para quando nada é importado */}
                    {importResult.success_count === 0 && (!importResult.failures || importResult.failures.length === 0) && (
                        <Alert variant="warning">
                            Nenhum paciente foi importado. Verifique se o arquivo está preenchido corretamente ou se os pacientes já existem no sistema.
                        </Alert>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImportacaoPacientesTab;